import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { thumbUrl } from '@/lib/imageUrl';
import { ArrowLeft, Plus, MessageCircle, Eye, Clock, Pin, Lock, Search } from 'lucide-react';
import { isPaidTier } from '@/lib/tiers';

const CAT_NAMES = { tiles: 'Tiles', accessories: 'Accessories', nmjl: 'NMJL Card Questions', other: 'Other Mahjong Questions' };
const CAT_COLORS = { tiles: '#56dfb4', accessories: '#56c6df', nmjl: '#df5681', other: '#b456df' };

export default function ForumCategoryPage() {
  const { categoryId } = useParams();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('newest');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const isPaid = isPaidTier(user);
  const catName = CAT_NAMES[categoryId] || categoryId;
  const catColor = CAT_COLORS[categoryId] || '#56dfb4';

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ category: categoryId, sort, page: String(page) });
    if (search) params.set('search', search);
    api.get(`/forum/threads?${params.toString()}`).then(({ data }) => {
      setThreads(data.threads || []);
      setTotal(data.total || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [categoryId, sort, page, search]);

  return (
    <div data-testid="forum-category-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/forum" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Forum
      </Link>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: catColor }}>{catName}</h1>
          <p className="text-slate-500 mt-1">{total} threads</p>
        </div>
        {isPaid && (
          <Link to={`/forum/new?category=${categoryId}`}>
            <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-2" data-testid="new-thread-cat-btn">
              <Plus className="w-4 h-4" /> New Thread
            </Button>
          </Link>
        )}
      </div>

      {/* Sort and Search */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex gap-1">
          {[{ v: 'newest', l: 'Newest' }, { v: 'most_active', l: 'Most Active' }, { v: 'most_viewed', l: 'Most Viewed' }].map(s => (
            <button key={s.v} onClick={() => { setSort(s.v); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${sort === s.v ? 'text-white border-transparent' : 'border-slate-200'}`}
              style={sort === s.v ? { backgroundColor: catColor } : {}}>
              {s.l}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search threads..." className="pl-9 rounded-xl text-sm h-8" data-testid="forum-search" />
        </div>
      </div>

      {/* Threads */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: catColor, borderTopColor: 'transparent' }} /></div>
      ) : threads.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No threads yet</p>
          {isPaid && <Link to={`/forum/new?category=${categoryId}`} className="text-sm mt-2 block" style={{ color: catColor }}>Start the first discussion</Link>}
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map(t => (
            <Link key={t.thread_id} to={`/forum/thread/${t.thread_id}`}>
              <Card className="border-slate-100 hover:bg-slate-50 transition-colors mb-2" data-testid={`thread-${t.thread_id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {t.author_photo ? (
                      <img src={thumbUrl(t.author_photo)} alt={t.author_name} loading="lazy" decoding="async" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#56dfb4]/15 flex items-center justify-center text-xs font-bold">
                        {(t.author_name || 'U')[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {t.pinned && <Pin className="w-3 h-3 text-amber-500" />}
                      {t.locked && <Lock className="w-3 h-3 text-slate-400" />}
                      <h3 className="font-medium text-sm truncate">{t.title}</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                      <span>by {t.author_name}</span>
                      <span>{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                      {t.tags?.map(tag => <Badge key={tag} variant="outline" className="text-[10px] rounded-full px-1.5 py-0">{tag}</Badge>)}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-400 flex-shrink-0">
                    <div className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{t.reply_count || 0}</div>
                    <div className="flex items-center gap-1"><Eye className="w-3 h-3" />{t.views || 0}</div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
